'use client';
import { CaptchaRequestMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import CustomHeading from '../custom/CustomHeading';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section id="newsletters" className={cn('bg-[#EBECFF] p-2 sm:p-12', 'ea-homepage-newsletter')}>
      <form
        className={cn('grid grid-cols-12 gap-6 py-2', 'ea-homepage-newsletter__form')}
        onSubmit={handleSubmit}
      >
<CaptchaRequestMessage error={subscribeMutation.error} />
        <div
          className={cn(
            'col-span-12 flex flex-row items-center justify-start space-x-4 md:col-span-7 md:pr-7',
            'ea-homepage-newsletter__form-icon-email',
          )}
        >
          <div className={cn('flex flex-col gap-2', 'ea-homepage-newsletter__form-title')}>
            <CustomHeading
              heading1="Newsletter"
              heading2="NEZMEŠKAJTE ŽIADNE NOVINKY A AKTUALITY"
            />
            <p
              className={cn(
                'text-sm text-muted-foreground',
                'ea-homepage-newsletter__form-title-sub',
              )}
            >
              {t('text609')}
            </p>
          </div>
        </div>
        <div
          className={cn(
            'col-span-12 flex flex-col justify-center space-y-3 md:col-span-5',
            'ea-homepage-newsletter__form-item-email',
          )}
        >
          <div className={cn('flex flex-row items-center gap-x-3')}>
            <Input
              aria-label={systemLabels('email')}
              id="newsletter-email"
              type="email"
              name="email"
              placeholder={t('text183')}
              onChange={(e) => setEmail(e.target.value)}
              className={{
                wrapper: cn(
                  'gap-x-3 rounded-none border-none bg-white pr-1 text-muted-foreground',
                  'ea-homepage-newsletter__form-item-email-input',
                ),
              }}
            />
            <Button
              type="submit"
              className={cn(
                'hidden rounded-none border-none bg-primary px-4 text-primary-foreground md:block',
                'ea-homepage-newsletter__form-item-email-submit-button',
              )}
              disabled={email.length === 0 || !checked || isSubscribed}
              isLoading={subscribeMutation.isPending}
            >
              {t('text182')}
            </Button>
          </div>
          {isSubscribed && <span className={cn('text-success')}>{t('text234')}</span>}
          <div
            className={cn(
              'flex flex-row items-center space-x-2',
              'ea-homepage-newsletter__form-item-gdpr',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => setChecked(!checked)}
              id="newsletter-gdpr"
              aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
              className={cn('rounded-none', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
              required
            />
            <Label
              htmlFor="newsletter-gdpr"
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-label')}
            >
              <span className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-span')}>
                {t('text264')}&nbsp;
                <a
                  href={t('text391')}
                  className={cn(
                    'text-primary',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link1',
                  )}
                >
                  {t('text265')}
                </a>{' '}
                <a
                  href={t('text392')}
                  className={cn(
                    'text-primary',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link2',
                  )}
                >
                  {t('text436')}
                </a>
                .
              </span>
            </Label>
          </div>
          <Button
            type="submit"
            className={cn(
              'w-full rounded-none border-none bg-primary px-4 text-primary-foreground md:hidden',
              'ea-homepage-newsletter__form-item-email-submit-button',
            )}
            disabled={email.length === 0 || !checked || isSubscribed}
            isLoading={subscribeMutation.isPending}
          >
            {t('text182')}
          </Button>
        </div>
      </form>
    </section>
  );
}
